import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";

const heroSlides = [
  {
    title: "Hola World Migration Australia",
    description:
      "Expert guidance for PR, skilled visas, and migration pathways. Trusted migration consultants helping students and professionals settle in Australia.",
    image: "/assets/home-hero/hero-banner-01.jpg",
    mobileImage: "/assets/home-hero/mobile/hero-banner-01.jpg",
    objectPosition: "70% 50%",
  },
  {
    title: "Australia PR & Skilled Migration Experts",
    description:
      "Your trusted partner for PR consultation, skilled migration pathways, and visa documentation support throughout your Australia journey.",
    image: "/assets/home-hero/hero-banner-02.jpg",
    objectPosition: "60% 50%",
  },
  {
    title: "Start Your New Beginning in Australia",
    description:
      "Complete visa consultancy services from initial assessment to settlement support. Making your Australian migration smooth and stress-free.",
    image: "/assets/home-hero/hero-banner-03.jpg",
    objectPosition: "center",
  },
];

export default function HeroCarousel() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  const [isBookingPressed, setIsBookingPressed] = useState(false);
  const [isEligibilityPressed, setIsEligibilityPressed] = useState(false);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 400], [0, 80]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };
    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % heroSlides.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const handleBookConsultationClick = () => {
    setIsBookingPressed(true);
    setTimeout(() => setIsBookingPressed(false), 150);
    // The button's data-cal-namespace/data-cal-link/data-cal-config attributes
    // are picked up automatically by Cal.com's embed click listener, which
    // opens the booking modal — no manual Cal() call needed here.
  };

  const handleEligibilityClick = () => {
    setIsEligibilityPressed(true);
    setTimeout(() => navigate("/australia-migration-eligibility-checker"), 100);
  };

  const goToSlide = (index: number) => setCurrentIndex(index);
  const activeSlide = heroSlides[currentIndex];

  return (
    <section id="hero" className="w-full overflow-hidden relative bg-slate-900">
      <motion.div className="absolute inset-0" style={isMobile ? undefined : { y }}>
        {heroSlides.map((slide, index) => (
          <img
            key={`${slide.image}-${index}`}
            src={isMobile && (slide as any).mobileImage ? (slide as any).mobileImage : slide.image}
            alt={slide.title}
            loading={index === 0 ? "eager" : "lazy"}
            fetchPriority={index === 0 ? "high" : "auto"}
            decoding="async"
            className={`absolute inset-0 h-full w-full object-cover transition-all duration-[900ms] ease-in-out ${
              currentIndex === index ? "opacity-100 scale-100" : "opacity-0 scale-105"
            }`}
            style={{ objectPosition: isMobile ? "center" : (slide as any).objectPosition || "center" }}
            onError={(event) => {
              const target = event.currentTarget;
              const alreadyTried = target.getAttribute("data-error-tried");
              if (!alreadyTried) {
                target.setAttribute("data-error-tried", "1");
                const src = target.src;
                if (src.includes(".jpg")) {
                  target.src = src.replace(".jpg", ".jpeg");
                } else if (src.includes(".jpeg")) {
                  target.src = src.replace(".jpeg", ".jpg");
                }
              }
            }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/50 via-slate-900/22 to-slate-900/8" />
      </motion.div>

      <div className="relative z-10 mx-auto flex min-h-[82vh] md:min-h-screen lg:min-h-screen max-w-7xl items-center px-5 md:px-8 lg:px-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSlide.title}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="max-w-2xl pt-20 md:pt-24 pb-16"
          >
            <p className="inline-flex items-center rounded-full border border-white/25 bg-white/10 px-4 py-1 text-xs font-medium tracking-wide text-white/90 backdrop-blur-sm">
              Trusted Migration Experts
            </p>

            <h1 className="mt-5 text-4xl leading-tight md:text-6xl md:leading-[1.1] font-semibold text-white">
              {activeSlide.title}
            </h1>

            <p className="mt-5 text-base md:text-lg text-white/85 max-w-xl">
              {activeSlide.description}
            </p>

            <div className="flex flex-wrap items-center gap-4 mt-9 text-sm">
                  <button
                    onClick={handleEligibilityClick}
                    className={`rounded-full px-7 h-12 font-semibold cursor-pointer transform transition-all duration-75 ${
                      isEligibilityPressed
                        ? "scale-[0.93] shadow-inner bg-slate-200 text-slate-800"
                        : "bg-white text-slate-900 hover:bg-slate-100"
                    }`}
                  >
                    Free Eligibility Check
                  </button>
              <button
                type="button"
                onClick={handleBookConsultationClick}
                data-cal-namespace="consultation-session"
                data-cal-link="hola-world/consultation-session"
                data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}'
                className={`flex items-center justify-center gap-2 border-2 rounded-full px-6 h-12 font-semibold cursor-pointer transform transition-all duration-75 ${
                  isBookingPressed
                    ? "scale-[0.93] shadow-inner border-blue-300 bg-blue-600/40 text-blue-50"
                    : "border-white/60 bg-gradient-to-r from-blue-500/30 to-white/10 hover:border-white hover:from-blue-500/50 hover:to-white/20 hover:shadow-lg hover:shadow-blue-500/25 text-white"
                }`}
              >
                <Calendar className="w-5 h-5" />
                <span>Book Consultation</span>
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="absolute bottom-6 left-1/2 z-20 -translate-x-1/2 flex items-center justify-center gap-2">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            type="button"
            aria-label={`Go to slide ${index + 1}`}
            onClick={() => goToSlide(index)}
            className={`h-2.5 rounded-full transition-all ${
              currentIndex === index ? "w-8 bg-white" : "w-2.5 bg-white/45"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
